'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useId } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Button, Field, Input } from '@kiao-voice/ui-kit'
import {
  validateCoupon as validateCouponDefault,
  type CouponProductKind,
  type ValidateCouponPayload,
} from './api-client'
import { couponReasonMessage } from './coupon-reason-message'
import { readStoredToken } from '../auth/auth-storage'
import type { PersonalPaidPlanCode, SubscriptionCycle } from '@kiao-voice/core'

export interface AppliedCoupon {
  code: string
  /** TWD discount the server resolved for this checkout (capped at base). */
  twd: number
}

interface CouponFieldProps {
  product: CouponProductKind
  /** Identifies what's being bought so the server can resolve the base price. */
  planCode?: PersonalPaidPlanCode
  cycle?: SubscriptionCycle
  packCode?: string
  seats?: number
  /** Lifted to the modal so it can recompute the breakdown + send `couponCode`,
   *  and clear it if the submit-time re-validation rejects (422). */
  applied: AppliedCoupon | null
  onApplied: (coupon: AppliedCoupon) => void
  onCleared: () => void
  disabled?: boolean
  /** When false, the built-in "折抵券" label is hidden — the checkout panel renders
   *  its own mono section title above the field. Defaults to true. */
  showLabel?: boolean
  /** Test seam. */
  validateFn?: typeof validateCouponDefault
}

const couponFormSchema = z.object({
  code: z.string().trim().min(1),
})

type CouponFormValues = z.infer<typeof couponFormSchema>

/**
 * Reusable 折抵券 input for the checkout modals. Soft-validates against
 * `/billing/coupons/validate` (preview only) and lifts the resolved discount up;
 * the charge site re-checks authoritatively under lock at submit. Shown by
 * default; if the server's `coupon.enabled` kill-switch is off, the validate
 * call reports codes unavailable and a submit-time code is refused.
 */
export function CouponField({
  product,
  planCode,
  cycle,
  packCode,
  seats,
  applied,
  onApplied,
  onCleared,
  disabled,
  showLabel = true,
  validateFn = validateCouponDefault,
}: CouponFieldProps) {
  const { t } = useTranslation('web')
  const inputId = useId()
  const errorId = `${inputId}-error`
  const {
    clearErrors,
    handleSubmit,
    register,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CouponFormValues>({
    defaultValues: { code: '' },
    resolver: zodResolver(couponFormSchema),
  })
  const code = watch('code')
  const error = errors.code?.message ?? null

  const reasonMessage = (reason?: string): string =>
    couponReasonMessage(t, reason ?? 'unknown', 'billing.couponInvalid', {
      signedIn: readStoredToken() !== null,
    })

  const apply = handleSubmit(async ({ code: submittedCode }) => {
    try {
      const payload: ValidateCouponPayload = {
        code: submittedCode,
        product,
        planCode,
        cycle,
        packCode,
        seats,
      }
      const result = await validateFn(payload)
      if (result.valid) {
        reset()
        onApplied({ code: result.code ?? submittedCode.toUpperCase(), twd: result.couponTwd })
      } else {
        setError('code', { type: 'server', message: reasonMessage(result.reason) })
      }
    } catch (err) {
      reportHandledException(err, { source: 'features/billing/coupon-field.tsx' })
      // Network/availability error — the soft preview is best-effort. REPORT it
      // (no silent catch — CLAUDE.md error policy) so a systematic validate
      // failure is visible, then surface a generic message; the user can still
      // proceed (the charge site re-validates authoritatively at submit).
      console.error('coupon preview validation failed', err)
      setError('code', { type: 'server', message: t('billing.couponCheckFailed') })
    }
  })

  const remove = () => {
    reset()
    onCleared()
  }

  if (applied) {
    return (
      <div className="checkout-coupon-applied" data-testid="coupon-applied">
        <span>{t('billing.couponAppliedLabel', { code: applied.code })}</span>
        <Button
          variant="ghost"
          size="sm"
          className="px-0 underline"
          data-testid="coupon-remove"
          disabled={disabled}
          onClick={remove}
        >
          {t('billing.couponRemove')}
        </Button>
      </div>
    )
  }

  return (
    <div data-testid="coupon-field">
      <Field
        label={showLabel ? t('billing.couponLabel') : undefined}
        htmlFor={showLabel ? inputId : undefined}
        error={error}
        errorId={errorId}
      >
        <div className="checkout-coupon">
          <Input
            id={inputId}
            type="text"
            disabled={disabled || isSubmitting}
            placeholder={t('billing.couponPlaceholder')}
            aria-label={showLabel ? undefined : t('billing.couponLabel')}
            data-testid="coupon-input"
            {...register('code', {
              onChange: () => clearErrors('code'),
            })}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                void apply()
              }
            }}
          />
          <Button
            variant="secondary"
            size="control"
            data-testid="coupon-apply"
            disabled={disabled || isSubmitting || code.trim().length === 0}
            onClick={() => void apply()}
          >
            {isSubmitting ? t('billing.couponApplying') : t('billing.couponApply')}
          </Button>
        </div>
      </Field>
    </div>
  )
}
